/**
 * **打印包的节点** —— 页面节点 + 组件节点。
 *
 * ## 为什么打印能力要长成「节点」而不是「另一份文档模型」
 *
 * `print-template-architecture.md` D2 定的形状：
 *
 * > **双视图、单一真源**：节点图与画布**都能**编辑版面；
 * > 元素节点的几何是**参数**，不是连线。
 *
 * 也就是说：**页面的真源就是节点图里的节点**。页面编辑器（`print.page`
 * 那个窗口）是它的一个**视图**，不是另一个存储 —— 这正是用户 2027 说的
 * 「节点信息变了页面编辑器也变，页面编辑器变节点数据也更新」。
 *
 * ## 三种节点，各管一段
 *
 * ```text
 *   print.page              页面：纸张尺寸 / dpi / 边距 + 它收集的组件
 *     └─ items（变参行）     接了哪些组件 —— **组成关系靠连线**（数据流）
 *   print.component.text    文本组件：内容 + 几何（mm）
 *   print.component.box     矩形组件：几何（mm）+ 线宽
 * ```
 *
 * ⚠️ **只有「组成关系」走连线，「位置」永远走参数**（§2.1 末尾那条禁令）：
 * 如果 `A → B` 表示「B 排在 A 下面」，那就是在做约束求解布局引擎 ——
 * 复杂度和「不能所见即所得」是它的必然代价。
 * 所以 `items` 这条边表达的是「这个页面上有这些组件」，而不是「谁在谁右边」。
 *
 * ⚠️ 几何参数用 `property` 行（**没有端口**）：摆位置是设计期的动作，
 * 不是数据流。把它做成 `input` 会多出一个能接线却什么都不影响的点 ——
 * 常量族踩过同一个坑（见 `node/constNodes.ts` 的注释）。
 */
/* ⚠️ 只有**类型**来自内核（`import type` 不进产物）—— 包依赖契约，不依赖实现 */
import type { NodeTypeDef } from '@/node/package'
/* ⚠️ 符号表是**数据**：节点上的枚举控件直接列它，加符号只改 barcode.ts 一处 */
import { SYMBOLOGIES } from './barcode'
import { rowsForComponent } from './params'

/**
 * **行结构由参数表生成**（`params.ts`）。
 *
 * 这个文件以前手写着每个节点的每一行，而侧栏面板又手写了一遍同样的
 * label / 值域 / 单位 —— 两份会漂（节点上拖不动、面板里能填）。
 * 现在两边都读 `PARAMS` 那一张表。
 *
 * 节点定义里只剩三样**节点自己的事**：
 *   · 标题 / 说明 / 关键词（菜单与提示用）；
 *   · 分类（菜单里一档）；
 *   · 它的**参数面**由哪个组件种类决定（`rowsForComponent('barcode')`）。
 */
/**
 * **文档**：一份打印模板的根 —— 纸张（尺寸 / 分辨率 / 边距）+ 它有哪些页。
 *
 * ⚠️ 纸张参数从**页面节点搬到了这里**（真机反馈："一个打印模板，它需要是一个
 * 文档，然后文档中不只是一个页面，它可能是多页的；页面节点的纸张宽高可以
 * 转移到文档的整体属性中"）。一张纸的"多大、多少 dpi"是一份文档的属性 ——
 * 十页纸各自一套 dpi 是没人想要的东西。
 *
 * 页面节点因此只剩下"装了哪些组件"这一件事。
 */
export const DOCUMENT_NODE_TYPE = 'print.document'
export const PAGE_NODE_TYPE = 'print.page'
export const TEXT_NODE_TYPE = 'print.component.text'
export const BOX_NODE_TYPE = 'print.component.box'
export const BARCODE_NODE_TYPE = 'print.component.barcode'
export const QRCODE_NODE_TYPE = 'print.component.qrcode'
export const IMAGE_NODE_TYPE = 'print.component.image'

/** 组件节点的公共收尾：一路 `component` 输出 */
const COMPONENT_OUT = {
  kind: 'output' as const,
  id: 'out',
  label: '组件',
  /* ⚠️ 精确：组件输出不该被当成"任意 layout"用（那会把页面也放进来） */
  socket: { id: 'out', type: 'component' as const, strict: true },
}

const ONE_D = SYMBOLOGIES.filter((spec) => spec.kind === '1d').map((spec) => ({ id: spec.id, label: spec.label }))
const TWO_D = SYMBOLOGIES.filter((spec) => spec.kind === '2d').map((spec) => ({ id: spec.id, label: spec.label }))

/**
 * 一维 / 二维各取**第一个符号**当默认，并用它的 `sample` 当默认内容 ——
 * 新建的条码节点必须是"一放下来就能编码成功"的（见 `params.ts` 的 `defaults`）。
 */
const DEFAULT_1D = SYMBOLOGIES.find((spec) => spec.kind === '1d')!
const DEFAULT_2D = SYMBOLOGIES.find((spec) => spec.kind === '2d')!
export const BARCODE_DEFAULTS = {
  text: DEFAULT_1D.sample,
  symbology: DEFAULT_1D.id,
  xDimMicron: 375,
  /* ⚠️ 宽 0 = 自动（按 X 维度 × 模块数）；高 = **含 HRI 的总高** */
  wMm: 0,
  hMm: 15,
}
export const QRCODE_DEFAULTS = {
  text: DEFAULT_2D.sample,
  symbology: DEFAULT_2D.id,
  xDimMicron: 500,
  /* 二维码：宽高任一为 0 时按 X 维度算，两个都写以宽为准 */
  wMm: 0,
  hMm: 0,
}

/**
 * **图标**（24×24 viewBox 的 stroke 路径，三条约束见 `node/nodeIcons.ts` 的文件头）。
 *
 * ⚠️ 为什么每个组件类型都值得一个（而不是靠分类图标兜底）：用户原话是
 * 「改了名字不能一眼看懂这是什么节点」—— 打印包里五个组件节点的名字**都会**被改
 * （"收件人""店名""价签码"），而它们的分类是同一个「热敏打印」：
 * 全靠分类图标的话，五个节点的头部长得一模一样。
 *
 * ⚠️ **路径只用绝对命令**（`M` / `L` / `H` / `V` / `C` / `A` / `Z`）：相对命令
 * （`h5` / `l-2 3`）的增量不在 "所有数字都落在 0–24" 这条判据的射程内，
 * 而那条判据是 `verify:print` 唯一能自动查"画到框外面去了"的手段。
 *
 * ⚠️ 只有 `path` / `rect` / `circle` 这些**基本图元**、只有 `d` / 几何属性：
 * 颜色与线宽由渲染侧的 CSS 统一给（`fill: none; stroke: currentColor;
 * stroke-width: 1.8`）—— 路径里写 `fill` / `stroke-width` 会让图标在悬停变色时
 * 露出一块死色，或者在别的线宽下糊成一团。
 */
const ICONS = {
  /* 文档：一张纸 + 右下角一个"作业已完成"的标记（这份模板是要出纸的） */
  document:
    '<path d="M4.5 2.5H11.5L15 6V19H4.5z"/><path d="M11.5 2.5V6H15"/><path d="M7 10H12M7 13.5H10"/><circle cx="18.4" cy="18.2" r="3.2"/><path d="M17 18.3L18 19.3 19.9 17.1"/>',
  /* 页面：一张纸 + 里面的边距框（静区 / 不可打印区是版面的一部分） */
  page: '<rect x="4.5" y="3" width="15" height="18" rx="1.5"/><rect x="7.5" y="6.5" width="9" height="11"/>',
  /* 文本：一个 T（字脚留着，免得在 14px 下和"十字"分不开） */
  text: '<path d="M5 6H19M12 6V18M9 18H15"/>',
  /* 矩形：一个方框（和文本的 T 明显不同） */
  box: '<rect x="3.5" y="5.5" width="17" height="13" rx="1.5"/>',
  /* 条码：一排条 + 下面那行人眼可读字符（用短横示意） */
  barcode: '<path d="M4 4V13M7 4V13M10 4V10M13.5 4V13M17 4V10M20 4V13"/><path d="M7 17.5H9.5M12 17.5H14M17 17.5H18.5"/>',
  /* 二维码：三个定位角 + 剩下的点阵 */
  qrcode:
    '<path d="M4 4H10V10H4z"/><path d="M14 4H20V10H14z"/><path d="M4 14H10V20H4z"/><path d="M14 14H16.5V16.5H14z"/><path d="M19.5 14V16.5"/><path d="M14 19.5H19.5"/><path d="M19.5 19.5h.01"/>',
  /* 图片：相框 + 山 + 太阳（和工具条那个「+ 图片」同一个意思） */
  image: '<rect x="3.5" y="5" width="17" height="14" rx="2"/><path d="M4 16.5L8.5 12 11.5 15 15 11.5 20 16.5"/><circle cx="9" cy="9.3" r="1.3"/>',
} as const

const DOCUMENT_NODE: NodeTypeDef = {
  type: DOCUMENT_NODE_TYPE,
  title: '热敏打印 · 文档',
  category: 'print',
  icon: ICONS.document,
  width: 230,
  hint: '一份模板：纸张（尺寸 / 分辨率 / 边距）+ 若干页。页面接到「页面」这一行上',
  keywords: ['print', 'document', 'template', 'paper', 'size', '文档', '模板', '纸张', '多页'],
  rows: [
    ...rowsForComponent('document'),
    /*
     * ⚠️ 页是**变参行**（和页面的组件一样）：顺序 = `pages.N` = 第几页。
     * 契约：一条数据流（文档的求值结果 = 这些页的求值结果）。
     */
    {
      kind: 'repeater',
      id: 'pages',
      label: '页面',
      socketType: 'page',
      /* ⚠️ **精确连接**：这里只收"页"（组件虽然共享 layout 祖先，但不是一页） */
      strict: true,
      min: 1,
      max: 64,
    },
    {
      kind: 'output',
      id: 'out',
      label: '文档',
      socket: { id: 'out', type: 'page', strict: true },
    },
  ],
}

const PAGE_NODE: NodeTypeDef = {
  type: PAGE_NODE_TYPE,
  title: '热敏打印 · 页面',
  category: 'print',
  icon: ICONS.page,
  width: 220,
  hint: '一张纸：尺寸 / 分辨率 / 边距，以及它上面有哪些组件（把组件接到「组件」这一行）',
  keywords: ['print', 'page', 'paper', 'label', '页面', '纸张', '标签', '版面'],
  rows: [
    /*
     * ⚠️ **组成关系走连线**（变参行），因为它确实是一条数据流：
     * 页面的求值结果 = 这些组件的求值结果。位置仍然在组件的参数里。
     */
    {
      kind: 'repeater',
      id: 'items',
      label: '组件',
      socketType: 'component',
      /*
       * ⚠️ **精确连接**：只收组件。不标的话，`component` 与 `page` 共享
       * `layout` 祖先 —— 于是"一张纸"也能塞进「组件」行（值还会被裁成
       * layout），而拖出去高亮的那一堆端点里大部分都不该连
       * （真机反馈："它可以连接的节点端点有点多，而且它都不符合连接需求"）。
       */
      strict: true,
      min: 1,
      max: 64,
    },
    /*
     * ⚠️ 页面节点**没有纸张参数**：纸张在文档上（见 `DOCUMENT_NODE`）。
     * 它自己只剩"装了哪些组件"。
     * （旧文档里可能存在页面上的纸张值 —— `pagesOf` 仍会读它们当兜底，
     * 那是迁移路径，不是界面上的控件。）
     */
    {
      kind: 'output',
      id: 'out',
      label: '页面',
      socket: { id: 'out', type: 'page', strict: true },
    },
  ],
}

const TEXT_NODE: NodeTypeDef = {
  type: TEXT_NODE_TYPE,
  title: '热敏打印 · 文本',
  category: 'print',
  icon: ICONS.text,
  width: 230,
  hint: '一段文本：内容可内联可接线；位置 / 尺寸 / 旋转在「布局」里，字体在外观里',
  keywords: ['print', 'component', 'text', 'label', '文本', '文字', '标签'],
  rows: [...rowsForComponent('text'), COMPONENT_OUT],
}

const BOX_NODE: NodeTypeDef = {
  type: BOX_NODE_TYPE,
  title: '热敏打印 · 矩形',
  category: 'print',
  icon: ICONS.box,
  width: 230,
  hint: '一个矩形框（线宽 / 圆角 / 填充）—— 标签上的表格线 / 分区框都靠它',
  keywords: ['print', 'component', 'box', 'rect', 'line', '矩形', '框', '线'],
  rows: [...rowsForComponent('box'), COMPONENT_OUT],
}

const BARCODE_NODE: NodeTypeDef = {
  type: BARCODE_NODE_TYPE,
  title: '热敏打印 · 条码',
  category: 'print',
  icon: ICONS.barcode,
  width: 240,
  hint: '一维条码：宽度由「X 维度」和编码结果算出来（不填宽），条高在「布局」里',
  keywords: ['print', 'component', 'barcode', 'ean', 'code128', '条码', '条形码', '扫码'],
  rows: [...rowsForComponent('barcode', { symbologies: ONE_D, defaults: BARCODE_DEFAULTS }), COMPONENT_OUT],
}

const QRCODE_NODE: NodeTypeDef = {
  type: QRCODE_NODE_TYPE,
  title: '热敏打印 · 二维码',
  category: 'print',
  icon: ICONS.qrcode,
  width: 240,
  hint: '二维码 / Data Matrix：**正方形**，边长由「X 维度」和编码结果算出来（拖任意一边都是改模块宽）',
  keywords: ['print', 'component', 'qrcode', 'datamatrix', '二维码', '扫码'],
  rows: [
    /*
     * ⚠️ 二维码是**另一个节点类型**，不是条码节点上的一个下拉选项
     * （`node/constNodes.ts` 的文件头记着同一条判据）：**行的结构不一样** ——
     * 它是正方形、没有条高、没有 HRI。做成一个节点加下拉的话，
     * 那两行会变成"改了没反应的控件"。
     */
    ...rowsForComponent('qrcode', { symbologies: TWO_D, defaults: QRCODE_DEFAULTS }),
    COMPONENT_OUT,
  ],
}

/**
 * 图片：**从矩形扩展**（只绘制，不要文字）。
 *
 * ⚠️ 默认 `src` 是**空的** —— 这不是偷懒：资产库还没做，图片只能是"地址"，
 * 而一个新的图片节点没有地址是正常的（它显示占位框 + 一句"还没有图像源"，
 * IR 里给一条 warning）。给它塞一张内置示例图反而会让人以为
 * "图片是内置的"。
 */
const IMAGE_NODE: NodeTypeDef = {
  type: IMAGE_NODE_TYPE,
  title: '热敏打印 · 图片',
  category: 'print',
  icon: ICONS.image,
  width: 230,
  hint: '位图（logo / 图形）：地址或 data: URI。热敏是单色的，所以有「二值化阈值」',
  keywords: ['print', 'component', 'image', 'logo', 'bitmap', '图片', '图像', 'logo'],
  rows: [
    ...rowsForComponent('image', { defaults: { wMm: 30, hMm: 20 } }),
    COMPONENT_OUT,
  ],
}

export const PRINT_NODE_TYPES: NodeTypeDef[] = [
  DOCUMENT_NODE,
  PAGE_NODE,
  TEXT_NODE,
  BOX_NODE,
  BARCODE_NODE,
  QRCODE_NODE,
  IMAGE_NODE,
]
